// One table, three bands. Sort headers sit once; each click reorders inside
// a band and never lifts a bank row above a request.
//
// Rows are the strategist board's: 34px, one line per cell, the board's own account, Usage,
// Vertical and Relationship cells (boardV2Columns.tsx), and the board's table styles
// (`densetable`). Flags and long text are chips and clamps with the full text on hover. A
// strategist without the queue grant gets the same table with only the bank band. With scores
// visible (the board's own rule, `useScores.ts`), Fit and Engagement follow Relationship as on the board,
// drawn by the board's own cell, for bank rows and picks alike.

import { useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import type { BankRequest, BookAccount, StrategistOption } from '../api/types';
import { displayAccountName, formatCount } from '../lib/format';
import { bankAgeShort, bankScoutTitle, scoutRead } from './bank.ts';
import {
  AccountCell as BoardAccountCell, Dash, RelationshipCell, ScoreCell, UsageCell, VerticalCell, WhyNowCell,
} from './boardV2Columns.tsx';
import type { CellContext } from './columns.ts';
import { ClaimCell, RequestReasonRow, canRequestRow } from './ClaimCell.tsx';
import {
  BAND_LABEL, type AvailableBand, type AvailableBands, type AvailableRow, competingAskCounts,
  rowName, visibleBands,
} from './bands.ts';
import { BankRouteControls, QueueRouteControls } from './ApprovalQueue.tsx';
import type { RankedAccount } from './derived.ts';
import { pickKindLabel, pickSignalLabel, sourceLabel } from './queueSignal.ts';
import { AccountChips, AccountHover, chipsAttribute } from './AccountHover.tsx';
import { requestFlagFacts } from './accountCard.ts';
import { RequestFlagChips } from './RequestFlagNotes.tsx';
import type { SortState } from './sort.ts';

interface AvailableColumn {
  key: string;
  label: string;
  hint?: string;
  width: string;
  // The width with the two score columns in, which the other columns narrow to pay for.
  scoredWidth: string;
  // Fit and Engagement: in only when scores are shown.
  isScore?: boolean;
}

// Pixel widths that sum to 964 with or without the score columns, so 1024 fits with the page's
// gutters. Wider screens spread the remainder in proportion to these, so Account, the widest,
// gains the most. (`calc()` in a `<col>` is ignored.) Account is wide because its name, chips and
// count share one line. With scores, the 122px of Fit (44) and Engagement (78, its header is one 69px word) come off the other columns, but
// not Route, whose Approve and Decline buttons need all of its 136px, nor Scout's read, whose
// header is one unbroken line that needs its 76px (68px clipped it to "Scout's rea" at 1024).
const COLUMNS: AvailableColumn[] = [
  { key: 'account', label: 'Account', width: '212px', scoredWidth: '164px' },
  // The board's Why now for a bank row; a pick's own signal (why it is here) for a pick.
  { key: 'signal', label: 'Why now', width: '104px', scoredWidth: '96px' },
  // Scout's one-line read of the account (`scoutRead`: the fit reason, else the saved research's
  // summary). The score behind it is retired from the page; the key stays `fit` so the sort
  // still orders by it.
  { key: 'fit', label: 'Scout’s read', width: '76px', scoredWidth: '76px' },
  { key: 'vertical', label: 'Vertical', width: '80px', scoredWidth: '70px' },
  { key: 'relationship_pill', label: 'Relationship', width: '88px', scoredWidth: '76px' },
  {
    key: 'fit_score', label: 'Fit', width: '44px', scoredWidth: '44px', isScore: true,
    hint: 'Fit, 0 to 100: should they buy voice AI from us. A * means some categories had no data and count at a default score.',
  },
  {
    key: 'engagement_score', label: 'Engagement', width: '78px', scoredWidth: '78px', isScore: true,
    hint: 'Engagement, 0 to 100: are they paying attention now. A * means some categories had no data and count at a default score.',
  },
  { key: 'credits_30', label: 'Usage', width: '54px', scoredWidth: '50px', hint: 'Playground credits in the last 30 days.' },
  {
    key: 'updated',
    label: 'Age',
    width: '44px',
    scoredWidth: '40px',
    hint: 'Days since Scout last wrote the account. Hover a row’s age for its state.',
  },
  {
    key: 'source', label: 'Source', width: '62px', scoredWidth: '56px',
    hint: 'Where the account came from: clay, q4_list, Request, ...',
  },
  { key: 'strategist', label: 'Strategist', width: '108px', scoredWidth: '78px' },
  { key: 'route', label: 'Route', width: '136px', scoredWidth: '136px' },
];

function columnsFor(queue: boolean, scoresShown: boolean): AvailableColumn[] {
  return COLUMNS
    .filter(column => queue || column.key !== 'strategist')
    .filter(column => scoresShown || !column.isScore)
    .map(column => (scoresShown ? { ...column, width: column.scoredWidth } : column));
}

// The board's cells take a ranked row. A bank row has no ranking, so it carries none.
function boardRowOf(account: BookAccount): RankedAccount {
  return { ...account, ranking: null, tag: null };
}

function SortableHeader({
  column,
  sort,
  onSort,
}: {
  column: AvailableColumn;
  sort: SortState;
  onSort: (key: string) => void;
}) {
  const sortable = column.key !== 'route';
  if (!sortable) {
    return (
      <th>
        <span className="visually-hidden">{column.label}</span>
      </th>
    );
  }

  const isActive = sort.key === column.key;
  const ariaSort = !isActive ? 'none' : sort.direction === -1 ? 'descending' : 'ascending';

  return (
    <th
      className={isActive ? 'on' : ''}
      aria-sort={ariaSort}
      role="columnheader"
      tabIndex={0}
      title={
        (column.hint ? column.hint + ' ' : '')
        + (isActive
          ? `Sorted ${sort.direction === -1 ? 'high to low' : 'low to high'}. `
            + 'Activate to reverse, again to return to worklist order.'
          : 'Activate to sort by this column. Currently in worklist order.')
      }
      onClick={() => onSort(column.key)}
      onKeyDown={event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onSort(column.key);
      }}
    >
      {column.label}
      {isActive ? <span className="sortarrow">{sort.direction === -1 ? '▼' : '▲'}</span> : null}
    </th>
  );
}

// Clicking a row opens the account, as on the board; a click on a link, button or select inside
// it does its own thing.
function openOnClick(event: MouseEvent<HTMLElement>, open: () => void) {
  const target = event.target as HTMLElement;
  if (target.closest('a, button, select, input, label')) return;
  open();
}

function openOnKey(event: KeyboardEvent<HTMLElement>, open: () => void) {
  if (event.target !== event.currentTarget) return;
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  open();
}

function accountIdOf(row: AvailableRow): string {
  return row.pick?.account_id || row.account?.id || row.id;
}

function AccountCell({
  row,
  context,
  strategists,
  competingTotal,
  request,
}: {
  row: AvailableRow;
  context: CellContext;
  strategists: StrategistOption[];
  competingTotal?: number;
  request?: BankRequest | null;
}) {
  const account = row.account;
  const pick = row.pick;
  const flagRequest = pick
    ? {
        exclusion_reason: pick.exclusion_reason,
        exclusion_detail: pick.exclusion_detail,
        reasons: pick.reasons,
        competing_count: null,
      }
    : (request ?? null);

  // The kind of pick ("Requested by Eva") is the hover; the band head already names the band.
  const title = pick ? pickKindLabel(pick, strategists) : undefined;

  // A pick whose account is not in the book yet has only its name and its request flags.
  const requestFacts = requestFlagFacts(flagRequest, competingTotal);
  return (
    <td title={title}>
      {account ? (
        <BoardAccountCell row={boardRowOf(account)} context={context} request={flagRequest} competingTotal={competingTotal} />
      ) : (
        <AccountHover row={null} name={rowName(row)} today={context.today} requestFacts={requestFacts}>
          <span className="dacct" data-chips={chipsAttribute(requestFacts.length)}>
            <span className="dname">{displayAccountName(rowName(row))}</span>
            <AccountChips count={requestFacts.length}>
              <RequestFlagChips request={flagRequest} competingTotal={competingTotal} />
            </AccountChips>
          </span>
        </AccountHover>
      )}
    </td>
  );
}

// Long text is one clamped line, with all of it on hover.
function ClampedCell({ text }: { text: string | null | undefined }) {
  if (!text) return <td><Dash /></td>;
  return <td title={text}><span className="dclip">{text}</span></td>;
}

// The cells the board draws and Available shares: Vertical, Relationship, Fit and Engagement (when
// shown), Usage.
function BoardCells({ account, context, scoresShown }: { account: BookAccount | null; context: CellContext; scoresShown: boolean }) {
  if (!account) {
    const count = scoresShown ? 5 : 3;
    return <>{Array.from({ length: count }, (_, index) => <td key={index}><Dash /></td>)}</>;
  }
  const boardRow = boardRowOf(account);
  return (
    <>
      <td><VerticalCell row={boardRow} context={context} /></td>
      <td><RelationshipCell row={boardRow} context={context} /></td>
      {scoresShown ? <td className="num"><ScoreCell row={boardRow} context={context} kind="fit" /></td> : null}
      {scoresShown ? <td className="num"><ScoreCell row={boardRow} context={context} kind="engagement" /></td> : null}
      <td><UsageCell row={boardRow} context={context} /></td>
    </>
  );
}

function UpdatedCell({ account, today }: { account: BookAccount | null; today: Date }) {
  if (!account) return <td><Dash /></td>;
  const title = bankScoutTitle(account, today);
  return <td title={title || undefined}>{bankAgeShort(account, today)}</td>;
}

// Where the account came from, as one short mono word. A new request's account may have none
// yet, so the pick's own source stands in.
function SourceCell({ account, pickSource }: { account: BookAccount | null; pickSource?: string | null }) {
  const source = account?.scout_source || pickSource;
  if (!source) return <td><Dash /></td>;
  const label = sourceLabel(source);
  return <td title={label}><span className="dclip mono">{label}</span></td>;
}

function PickRow({
  row,
  today,
  context,
  strategists,
  canApprove,
  busy,
  columnCount,
  competingTotal,
  scoresShown,
  onApprove,
  onDecline,
  onOpen,
}: {
  row: AvailableRow;
  today: Date;
  context: CellContext;
  strategists: StrategistOption[];
  canApprove: boolean;
  busy: boolean;
  columnCount: number;
  competingTotal: number;
  scoresShown: boolean;
  onApprove: (accountId: string, ownerEmail: string, requestedBy?: string | null, originalAccountId?: string) => void;
  onDecline: (accountId: string, reason: string, requestedBy?: string | null) => void;
  onOpen: (accountId: string) => void;
}) {
  const pick = row.pick;
  if (!pick) return null;
  const account = row.account;
  const isRequest = Boolean(pick.requested_by);
  const open = () => onOpen(accountIdOf(row));

  return (
    <QueueRouteControls
      rowClassName={'acct qrow' + (isRequest ? ' request' : ' proposal') + (competingTotal > 1 ? ' competing' : '')}
      columnCount={columnCount}
      pick={pick}
      strategists={strategists}
      canApprove={canApprove}
      busy={busy}
      onApprove={onApprove}
      onDecline={onDecline}
      onRowClick={event => openOnClick(event, open)}
      onRowKeyDown={event => openOnKey(event, open)}
    >
      <AccountCell
        row={row}
        context={context}
        strategists={strategists}
        competingTotal={isRequest ? competingTotal : undefined}
      />
      <ClampedCell text={pickSignalLabel(pick, strategists)} />
      <ClampedCell text={scoutRead(account)} />
      <BoardCells account={account} context={context} scoresShown={scoresShown} />
      <UpdatedCell account={account} today={today} />
      <SourceCell account={account} pickSource={pick.source} />
    </QueueRouteControls>
  );
}

function BankRow({
  row,
  today,
  context,
  queue,
  canApprove,
  canClaim,
  busy,
  columnCount,
  request,
  viewerEmail,
  strategists,
  scoresShown,
  onClaim,
  onRoute,
  onOpen,
}: {
  row: AvailableRow;
  today: Date;
  context: CellContext;
  queue: boolean;
  scoresShown: boolean;
  canApprove: boolean;
  canClaim: boolean;
  busy: boolean;
  columnCount: number;
  request: BankRequest | null;
  viewerEmail: string;
  strategists: StrategistOption[];
  onClaim: (account: BookAccount, reason: string) => void;
  onRoute: (account: BookAccount, ownerEmail: string) => void;
  onOpen: (accountId: string) => void;
}) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const account = row.account;
  if (!account) return null;
  const open = () => onOpen(accountIdOf(row));

  // The reason row shows only while this row still offers Request (a sent one closes it).
  const showReason = reasonOpen && !canApprove && canRequestRow(canClaim, request, account);

  return (
    <>
    <tr
      className="acct"
      tabIndex={0}
      onClick={event => openOnClick(event, open)}
      onKeyDown={event => openOnKey(event, open)}
    >
      <AccountCell row={row} context={context} strategists={[]} request={request} />
      {/* A bank row has no pick, so no signal of its own: the board's Why now. */}
      <td><WhyNowCell row={boardRowOf(account)} context={context} /></td>
      {/* Scout's fit reason, else the saved research's summary (a Q4 list account has no reason). */}
      <ClampedCell text={scoutRead(account)} />
      <BoardCells account={account} context={context} scoresShown={scoresShown} />
      <UpdatedCell account={account} today={today} />
      <SourceCell account={account} />
      {canApprove ? (
        <BankRouteControls
          account={account}
          strategists={strategists}
          canApprove={canApprove}
          busy={busy}
          onRoute={onRoute}
        />
      ) : (
        <>
          {queue ? <td><Dash /></td> : null}
          <td>
            <ClaimCell
              canClaim={canClaim}
              busy={busy}
              request={request}
              viewerEmail={viewerEmail}
              row={account}
              reasonOpen={showReason}
              onOpenReason={() => setReasonOpen(true)}
            />
          </td>
        </>
      )}
    </tr>
    {showReason ? (
      <RequestReasonRow
        columnCount={columnCount}
        busy={busy}
        accountName={account.account || account.domain || ''}
        onSend={why => onClaim(account, why)}
        onCancel={() => setReasonOpen(false)}
      />
    ) : null}
    </>
  );
}

function BandHead({ band, count, columnCount }: { band: AvailableBand; count: number; columnCount: number }) {
  const label = BAND_LABEL[band];
  return (
    <tr className="bandhead">
      <th scope="colgroup" colSpan={columnCount} id={band}>
        {label}{' '}
        <span className="bandcount">({formatCount(count)})</span>
      </th>
    </tr>
  );
}

interface BandActions {
  today: Date;
  context: CellContext;
  queue: boolean;
  strategists: StrategistOption[];
  canApprove: boolean;
  canClaim: (account: BookAccount) => boolean;
  requestFor: (accountId: string) => BankRequest | null;
  viewerEmail: string;
  claimingId: string | null;
  queueBusy: boolean;
  columnCount: number;
  scoresShown: boolean;
  onApprove: (accountId: string, ownerEmail: string, requestedBy?: string | null, originalAccountId?: string) => void;
  onDecline: (accountId: string, reason: string, requestedBy?: string | null) => void;
  onClaim: (account: BookAccount, reason: string) => void;
  onRoute: (account: BookAccount, ownerEmail: string) => void;
  onOpen: (accountId: string) => void;
}

export function AvailableTable({
  bands,
  sort,
  onSort,
  queue,
  scoresShown,
  ...actions
}: {
  bands: AvailableBands;
  sort: SortState;
  onSort: (key: string) => void;
  // Fit and Engagement, by the board's rule: scores visible and at least one account scored.
  scoresShown: boolean;
  // Whether the viewer holds the queue grant: the proposal bands, the band heads and the
  // Strategist column. Without it this is the bank, one band, headed by nothing.
  queue: boolean;
  today: Date;
  strategists: StrategistOption[];
  canApprove: boolean;
  canClaim: (account: BookAccount) => boolean;
  requestFor: (accountId: string) => BankRequest | null;
  viewerEmail: string;
  claimingId: string | null;
  queueBusy: boolean;
  onApprove: BandActions['onApprove'];
  onDecline: BandActions['onDecline'];
  onClaim: BandActions['onClaim'];
  onRoute: BandActions['onRoute'];
  onOpen: BandActions['onOpen'];
}) {
  const shown = visibleBands(bands);
  if (shown.length === 0) return null;

  const hasQueueBand = shown.some(band => band !== 'bank');
  const columns = columnsFor(queue, scoresShown);
  const context: CellContext = { today: actions.today, staleDays: 14 };

  return (
    <div className="boardv2">
      <p className="bankscrollhint">Scroll the table to see all details and act on an account.</p>
      <div
        className="tablewrap"
        style={{ marginTop: 12 }}
        tabIndex={0}
        role="region"
        aria-label="Available accounts"
        id={hasQueueBand ? 'proposals' : undefined}
      >
        <table className="densetable availabletable" style={{ width: columns.reduce((total, column) => total + parseInt(column.width, 10), 0) }}>
          <colgroup>
            {columns.map(column => <col key={column.key} style={{ width: column.width }} />)}
          </colgroup>
          <thead>
            <tr>
              {columns.map(column => (
                <SortableHeader
                  key={column.key}
                  column={column}
                  sort={sort}
                  onSort={onSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map(band => (
              <BandBlock
                key={band}
                band={band}
                rows={bands[band]}
                showHead={queue}
                {...actions}
                context={context}
                queue={queue}
                columnCount={columns.length}
                scoresShown={scoresShown}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BandBlock({
  band,
  rows,
  showHead,
  today,
  context,
  queue,
  strategists,
  canApprove,
  canClaim,
  requestFor,
  viewerEmail,
  claimingId,
  queueBusy,
  columnCount,
  scoresShown,
  onApprove,
  onDecline,
  onClaim,
  onRoute,
  onOpen,
}: BandActions & {
  band: AvailableBand;
  rows: AvailableRow[];
  showHead: boolean;
}) {
  const askCounts = competingAskCounts(rows);
  return (
    <>
      {showHead ? <BandHead band={band} count={rows.length} columnCount={columnCount} /> : null}
      {rows.map(row => (
        row.pick ? (
          <PickRow
            key={row.id}
            row={row}
            today={today}
            context={context}
            strategists={strategists}
            canApprove={canApprove}
            busy={queueBusy}
            columnCount={columnCount}
            competingTotal={row.pick.account_id ? (askCounts.get(row.pick.account_id) || 1) : 1}
            scoresShown={scoresShown}
            onApprove={onApprove}
            onDecline={onDecline}
            onOpen={onOpen}
          />
        ) : (
          <BankRow
            key={row.id}
            row={row}
            today={today}
            context={context}
            queue={queue}
            canApprove={canApprove}
            canClaim={row.account ? canClaim(row.account) : false}
            busy={claimingId === row.id}
            columnCount={columnCount}
            request={requestFor(row.id)}
            viewerEmail={viewerEmail}
            strategists={strategists}
            scoresShown={scoresShown}
            onClaim={onClaim}
            onRoute={onRoute}
            onOpen={onOpen}
          />
        )
      ))}
    </>
  );
}
